import {
  DocumentType,
  getApiDocumentsV1DocumentsListQueryKey,
  useApiDocumentsV1DocumentsList,
  useQueryClient,
} from 'api';
import { useTranslation } from 'i18n';
import { useState } from 'react';
import { Box, DocumentCard, MenuItem, Stack, Typography } from 'ui';

import { DOCUMENT_TYPE_DISPLAYED_VALUE } from '../../../document';
import { downloadFile } from '../../utils/utils';
import { History } from '../History/History';
import { UpdateAliasDialog } from '../UpdateAliasDialog/UpdateAliasDialog';

type Props = {
  contextObjectId: number;
};

export const DocumentList = ({ contextObjectId }: Props) => {
  const { t } = useTranslation('orders');
  const queryClient = useQueryClient();
  const { data: documents } = useApiDocumentsV1DocumentsList({ context_object_id: contextObjectId });

  const groups = Object.groupBy(documents?.results ?? [], ({ context_model_name }) => context_model_name);

  const [selectedDocumentId, setSelectedDocumentId] = useState<number | null>(null);
  const [updateAliasDialogOpen, setUpdateAliasDialogOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  const handleDownload = (url: string | null, name: string | null) => {
    downloadFile(url, name);
  };

  const handleOpenUpdateDialog = (documentId: number) => {
    setUpdateAliasDialogOpen(true);
    setSelectedDocumentId(documentId);
  };

  const handleCloseUpdateDialog = () => {
    setSelectedDocumentId(null);
    setUpdateAliasDialogOpen(false);
  };

  const handleHistoryDialogClose = () => {
    setHistoryOpen(false);
    setSelectedDocumentId(null);
  };

  const handleReview = (id: number) => {
    const url = new URL(`/ground-control/document-generation/${String(id)}`, window.origin);
    window.open(url.toString(), '_blank');
  };

  const selectedDocument = documents?.results.find((document) => document.id === selectedDocumentId);
  const historyTitle =
    selectedDocument?.alias || selectedDocument?.file_name || t('documentGeneration.history.values.untitledDocument');

  return (
    <Box
      sx={(theme) => ({
        backgroundColor: theme.palette.background.paper,
        height: '100%',
        maxHeight: 'calc(100vh - 66px)',
        mx: 'auto',
        overflowY: 'scroll',
        px: 3,
      })}
    >
      <Box sx={{ mb: 2, mt: 2.5 }}>
        <Typography
          fontWeight={700}
          sx={{
            fontSize: 20,
          }}
        >
          {t('documentGeneration.documentList.title')}
        </Typography>
      </Box>
      <Stack spacing={5}>
        {Object.entries(groups).map(([modelName, documents]) => (
          <Box key={modelName}>
            <Box sx={{ mb: 2, mt: 2.5 }}>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{
                  fontSize: 16,
                }}
              >
                {t('documentGeneration.documentList.groupTitle', {
                  values: { modelName: modelName.replace(/([A-Z])/g, ' $1').trim() },
                })}
              </Typography>
            </Box>
            <Stack spacing={1.5}>
              {documents?.map((document) => (
                <DocumentCard
                  key={document.id}
                  title={DOCUMENT_TYPE_DISPLAYED_VALUE[document.type as DocumentType]}
                  updatedAt={document.updated_at}
                  menuItems={[
                    <MenuItem key="review" onClick={() => handleReview(document.id)}>
                      {t('documentGeneration.documentList.actions.review')}
                    </MenuItem>,
                    <MenuItem key="update-alias" onClick={() => handleOpenUpdateDialog(document.id)}>
                      {t('documentGeneration.documentList.actions.updateAlias')}
                    </MenuItem>,
                  ]}
                  onDownload={() => handleDownload(document.file_url, document.file_name)}
                />
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
      <UpdateAliasDialog
        open={updateAliasDialogOpen}
        onClose={handleCloseUpdateDialog}
        documentId={selectedDocumentId ?? 0}
        alias={selectedDocument?.alias ?? ''}
        onSuccess={() => {
          queryClient.invalidateQueries({
            queryKey: getApiDocumentsV1DocumentsListQueryKey({ context_object_id: contextObjectId }),
          });
        }}
      />
      <History
        open={historyOpen}
        onClose={handleHistoryDialogClose}
        title={historyTitle}
        documentId={selectedDocumentId}
      />
    </Box>
  );
};
